import { ColDef, ColGroupDef, ColumnState, GridApi } from "ag-grid-community";

export type ContextMenuTarget = "header" | "cell" | "body";

/** 헤더 우클릭 DOM에서 col-id 추출 */
export const getHeaderColIdFromEventTarget = (target: EventTarget | null): string | null => {
	const el = (target as HTMLElement | null)?.closest?.(".ag-header-cell, .ag-header-group-cell");
	return el?.getAttribute("col-id") ?? null;
};

/** baseColumnDefs.colDef.pinned 기준 초기 pin Map (legacy 화면용) */
export const collectInitialPinnedColIds = (
	defs: (ColDef | ColGroupDef)[],
): Map<string, "left" | "right"> => {
	const pinnedMap = new Map<string, "left" | "right">();

	const walk = (items: (ColDef | ColGroupDef)[]) => {
		items.forEach((def) => {
			if ("children" in def && Array.isArray(def.children)) {
				walk(def.children);
				return;
			}
			const column = def as ColDef;
			if (!column.colId || !column.pinned) return;
			if (column.pinned === "left" || column.pinned === "right") {
				pinnedMap.set(column.colId, column.pinned);
			}
		});
	};

	walk(defs);
	return pinnedMap;
};

/** colDef 트리에서 leaf colId 목록 수집 */
export const collectColumnIdsFromDefs = (
	defs: (ColDef | ColGroupDef)[],
): string[] =>
	defs.flatMap((def) => {
		if ("children" in def && Array.isArray(def.children)) {
			return collectColumnIdsFromDefs(def.children);
		}
		const column = def as ColDef;
		return column.colId ? [column.colId] : [];
	});

/** defaultColumnState에서 colId → pinned Map */
export const buildPinnedMapFromColumnState = (
	defaultColumnState: ColumnState[],
): Map<string, ColumnState["pinned"]> =>
	new Map(defaultColumnState.map((state) => [state.colId, state.pinned ?? null]));

/** colDef + pinnedColIds로 최초 localStorage 시드용 ColumnState 생성 */
export const buildDefaultColumnStateFromDefs = (
	defs: (ColDef | ColGroupDef)[],
	pinnedColIds: readonly string[],
): ColumnState[] => {
	const pinnedSet = new Set(pinnedColIds);
	const result: ColumnState[] = [];

	const walk = (items: (ColDef | ColGroupDef)[]) => {
		items.forEach((def) => {
			if ("children" in def && Array.isArray(def.children)) {
				walk(def.children);
				return;
			}
			const column = def as ColDef;
			if (!column.colId) return;
			result.push({
				colId: column.colId,
				pinned: pinnedSet.has(column.colId) ? "left" : null,
				width: column.width,
				hide: column.hide ?? false,
			});
		});
	};

	walk(defs);
	return result;
};

/** 그룹 헤더 col-id → pin 대상 leaf colId */
export const resolvePinTargetColId = (api: GridApi, headerColId: string): string | null => {
	const displayedCols = api.getAllDisplayedColumns();
	if (!displayedCols.length) return null;

	const directIndex = displayedCols.findIndex((col) => col.getColId() === headerColId);
	if (directIndex >= 0) {
		return headerColId;
	}

	let lastLeafInGroup: string | null = null;
	for (const col of displayedCols) {
		let parent = col.getParent?.();
		while (parent) {
			const groupId = parent.getGroupId?.() ?? parent.getUniqueId?.();
			if (groupId === headerColId) {
				lastLeafInGroup = col.getColId();
			}
			parent = parent.getParent?.();
		}
	}
	if (lastLeafInGroup) {
		return lastLeafInGroup;
	}

	if (headerColId.startsWith("day_group_")) {
		const dayDd = headerColId.slice("day_group_".length);
		const day2Col = displayedCols.find((col) => col.getColId() === `day2_${dayDd}`);
		if (day2Col) return day2Col.getColId();
		const day1Col = displayedCols.find((col) => col.getColId() === `day1_${dayDd}`);
		if (day1Col) return day1Col.getColId();
	}

	return null;
};

/** Excel-style: headerColId까지 왼쪽 컬럼 고정 */
export const freezeColumnsToLeft = (api: GridApi, headerColId: string): boolean => {
	const targetColId = resolvePinTargetColId(api, headerColId);
	if (!targetColId) return false;

	const displayedCols = api.getAllDisplayedColumns();
	const targetIndex = displayedCols.findIndex((col) => col.getColId() === targetColId);
	if (targetIndex < 0) return false;

	const pinState = displayedCols.map((col, index) => ({
		colId: col.getColId(),
		pinned: index <= targetIndex ? ("left" as const) : null,
	}));

	return api.applyColumnState({ state: pinState, applyOrder: true });
};

/** 틀 고정 해제 — defaultColumnState 우선, 없으면 baseline colDef.pinned, 둘 다 없으면 전체 unpin */
export const unfreezeColumns = (
	api: GridApi,
	defaultColumnState?: ColumnState[],
	baselineDefs?: (ColDef | ColGroupDef)[],
): boolean => {
	let defaultPinned: Map<string, ColumnState["pinned"]>;

	if (defaultColumnState?.length) {
		defaultPinned = buildPinnedMapFromColumnState(defaultColumnState);
	} else if (baselineDefs) {
		defaultPinned = collectInitialPinnedColIds(baselineDefs);
	} else {
		defaultPinned = new Map();
	}

	const pinState = api.getColumnState().map((state) => ({
		colId: state.colId,
		pinned: defaultPinned.has(state.colId)
			? (defaultPinned.get(state.colId) ?? null)
			: null,
	}));

	return api.applyColumnState({ state: pinState, applyOrder: true });
};
